import { useCallback, useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import { CommonComment } from "@common/models/CommonComment.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";

import { useAdministrationDeps } from "../Administration";

interface Props {
  enabled?: boolean;
}

export const useCommonCommentsConsumer = ({ enabled = true }: Props) => {
  const { selectedWorkspace } = useAppSelectionContext();
  const { administrationService } = useAdministrationDeps();
  const [commonComments, setCommonComments] = useState<CommonComment[]>([]);
  const [loading, setLoading] = useState(false);

  const getCommonComments = useCallback(async () => {
    if (!selectedWorkspace || !enabled) return;

    try {
      setLoading(true);

      const { data } = await administrationService.getCommonComments(selectedWorkspace.id);
      setCommonComments(data);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "There was an error while fetching common comments",
      });
    } finally {
      setLoading(false);
    }
  }, [administrationService, selectedWorkspace, enabled]);

  const deleteCommonComment = async (id: string) => {
    setLoading(true);
    try {
      if (!selectedWorkspace) return;

      await administrationService.deleteCommonComment(selectedWorkspace.id, id);

      setCommonComments((comments) => {
        return comments.filter((comment) => comment.id !== id);
      });

      displayToast({
        content: "Common comment removed",
      });
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCommonComments();
  }, [getCommonComments]);

  return {
    loading,
    commonComments,
    getCommonComments,
    deleteCommonComment,
  };
};
